import {
  Pill,
  CirclePlus,
  Briefcase,
  LifeBuoy,
  Heart,
  Calendar,
  Users,
  UserCheck,
  CheckCircle,
  Loader2,
  Trash2,
  SquarePen,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  HealthRecordBoxProps,
  HealthRecordFromDataType,
  HealthRecordResponseType,
  PatientRecordPropsType,
  RecordFormsPropsType,
  RecordListPropsType,
} from "./type";
import { PatientService } from "../../services/api/patient.service";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";

export function PatientRecord({
  appointment,
  patient,
  editAccess,
  isTreatedPatient,
}: PatientRecordPropsType) {
  const [healthFormData, setHealthFormData] =
    useState<HealthRecordFromDataType>({
      id: "",
      health_parameter: "",
      health_value: "",
      health_value_description: "",
      status: "",
      current_state: "",
      daily_consumption: "",
      start_date: "",
      end_date: "",
    });

  const [isPatientAvailable, setPatientAvailable] = useState(false);
  const [selectedSocialHistory, setSelectedSocialHistory] = useState("Smoking");

  const [showForm, setShowForm] = useState({
    status: false,
    category: "",
  });

  const [fetchedHealthRecord, setFetchtedhealthRecord] =
    useState<HealthRecordResponseType>({});
  const [isLoading, setIsLoading] = useState(false);

  const loadHealthRecord = async () => {
    try {
      setIsLoading(true);
      const [patientAvailability, healthRecords] = await Promise.all([
        PatientService.getPatientAvailability(
          patient?.userId ||patient?.patientId ||appointment?.patient_id || appointment?.patientId 
        ),
        PatientService.getPatientHealthRecords(
          patient?.userId ||patient?.patientId||
            appointment?.patientDetails?.id ||
            appointment?.patientId
        ),
      ]);
      if (patientAvailability.success) {
        setPatientAvailable(patientAvailability.isEditable);
      }

      if (healthRecords.success) {
        setFetchtedhealthRecord(healthRecords.data);
      } else {
        toast.error(healthRecords.message);
        setFetchtedhealthRecord(healthRecords.data);
      }
    } catch (error) {
      toast.error(`Error while loading health record: ${error?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHealthRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MalehealthData = [
    {
      id: 0,
      category: "Allergies",
      icon: Pill, // Component reference
      primaryPlaceholder: "Enter Allergy Name",
      secondaryPlaceholder: "Enter Allergy Symptoms & Causes",
    },
    {
      id: 1,
      category: "Drug/Medications",
      icon: LifeBuoy,
      primaryPlaceholder: "Enter Medicine Name",
      secondaryPlaceholder: "Enter Medicine Uses",
    },
    {
      id: 2,
      category: "Medical Conditions",
      icon: Heart,
      primaryPlaceholder: "Enter Medical Condition Name",
      secondaryPlaceholder: "Enter Condition Details",
    },
    {
      id: 3,
      category: "Mental/Psychiatric History",
      icon: Calendar,
      primaryPlaceholder: "Enter Mental Health Condition",
      secondaryPlaceholder: "Enter Relevant History",
    },
    {
      id: 4,
      category: "Surgical History",
      icon: Briefcase,
      primaryPlaceholder: "Enter Surgery Name",
      secondaryPlaceholder: "Enter Surgery Details",
    },
    {
      id: 5,
      category: "Family Medical History",
      icon: Users,
      primaryPlaceholder: "Enter Family Member's Medical Condition",
      secondaryPlaceholder: "Enter Relationship and History",
    },
    {
      id: 6,
      category: "Social History",
      icon: UserCheck,
      primaryPlaceholder: "Enter Social History",
      secondaryPlaceholder: "Enter Relevant Social Factors",
    },
    {
      id: 7,
      category: "Vaccinations",
      icon: CheckCircle,
      primaryPlaceholder: "Enter Vaccination Name",
      secondaryPlaceholder: "Enter Vaccination Details",
    },
  ];

  const FemaleHealthData = [
    {
      id: 0,
      category: "Gynecologic Records",
      icon: Briefcase,
      primaryPlaceholder: "Enter Gynecologic Record",
      secondaryPlaceholder: "Enter Relevant History",
    },
    {
      id: 1,
      category: "Pregnancy Reports",
      icon: Heart,
      primaryPlaceholder: "Enter Pregnancy Report Details",
      secondaryPlaceholder: "Enter Pregnancy History",
    },
    {
      id: 2,
      category: "Menstrual Cycles",
      icon: Calendar,
      primaryPlaceholder: "Enter Menstrual Cycle Details",
      secondaryPlaceholder: "Enter Cycle History & Symptoms",
    },
    ...MalehealthData,
  ];

  const healthData =
    patient?.gender ||
    appointment?.patientDetails?.gender ||
    appointment?.gender === "Male"
      ? MalehealthData
      : FemaleHealthData;

  const handleOnChangeHealthFormData = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setHealthFormData({ ...healthFormData, [e.target.name]: e.target.value });
  };

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-5">
      {healthData.map((item, index) => {
        return (
          <RecordBox
            key={index}
            category={item.category}
            Icon={item.icon}
            primaryPlaceholder={item.primaryPlaceholder}
            secondaryPlaceholder={item.secondaryPlaceholder}
            onChangeFormData={handleOnChangeHealthFormData}
            healthFormData={healthFormData}
            healthRecords={fetchedHealthRecord[`${item.category}`]}
            patientId={
              patient?.userId ||patient?.patientId||
              appointment?.patientDetails?.id ||
              appointment?.patientId
            }
            isLoading={isLoading}
            loadHealthRecord={loadHealthRecord}
            setHealthFormData={setHealthFormData}
            showForm={showForm}
            setShowForm={setShowForm}
            selectedSocialHistory={selectedSocialHistory} // for social history checkbox
            setSelectedSocialHistory={setSelectedSocialHistory} // for social history checkbox
            isPatientAvailable={isPatientAvailable} // Pass isPatientAvailable to RecordBox
            isReadOnly={!editAccess}
            isTreatedPatient={isTreatedPatient}
          />
        );
      })}
    </div>
  );
}

//  Each box for storing and creating the new health record based on the category
const RecordBox = ({
  category,
  Icon,
  primaryPlaceholder,
  secondaryPlaceholder,
  onChangeFormData,
  healthFormData,
  patientId,
  healthRecords,
  isLoading,
  loadHealthRecord,
  setHealthFormData,
  showForm,
  setShowForm,
  selectedSocialHistory,
  setSelectedSocialHistory,
  isPatientAvailable, // Add this prop
  isReadOnly,
  isTreatedPatient,
}: HealthRecordBoxProps) => {
  const { user } = useAuth();
  const [isSaving, setIssaving] = useState(false);
  const [isEditable, setEditable] = useState(false);

  const handleShowForm = (formType: string, status: boolean) => {
    if (status) {
      setShowForm({ status: true, category: formType }); // Open only this form
    } else {
      setShowForm({ status: false, category: "" }); // Close form
    }
    setHealthFormData({
      id: "",
      health_parameter: "",
      health_value: "",
      health_value_description: "",
      status: "",
      current_state: "",
      daily_consumption: "",
      start_date: "",
      end_date: "",
    });
    setEditable(false);
  };

  // convert the state and end date to iso formate
  const handleISODateFormate = (date: string) => {
    if (!date) {
      return null;
    }
    const newDate = new Date(date);
    return newDate.toISOString();
  };
  // Handle Create or save health records
  const handleSaveHealthRecord = async (formType: string) => {
    setIssaving(true);

    const healthRecord = {
      health_parameter: formType,
      health_value:
        formType === "Social History"
          ? selectedSocialHistory
          : healthFormData.health_value,
      health_value_description: healthFormData.health_value_description,
      status: healthFormData.status || null,
      current_state: healthFormData.current_state || null,
      daily_consumption: healthFormData.daily_consumption || null,
      start_date: handleISODateFormate(healthFormData.start_date),
      end_date: handleISODateFormate(healthFormData.end_date),
    };
    try {
      const response = await PatientService.addPatientHealthRecord(
        patientId,
        healthRecord
      );
      if (response.success) {
        setShowForm({ status: false, category: "" });
        toast.success(response.message);
        loadHealthRecord();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(`Error while saving health record: ${error?.message}`);
    } finally {
      setIssaving(false);
    }
  };

  // handle Update save health records
  const handleUpdateHealthRecord = async (formType: string) => {
    setIssaving(true);
    const healthRecord = {
      health_parameter: formType,
      health_value:
        formType === "Social History"
          ? selectedSocialHistory
          : healthFormData.health_value,
      health_value_description: healthFormData.health_value_description,
      status: healthFormData.status || null,
      current_state: healthFormData.current_state || null,
      daily_consumption: healthFormData.daily_consumption || null,
      start_date: handleISODateFormate(healthFormData.start_date),
      end_date: handleISODateFormate(healthFormData.end_date),
    };

    try {
      const response = await PatientService.updatePatientHealthRecord(
        healthFormData?.id,
        healthRecord
      );
      if (response.success) {
        setShowForm({ status: false, category: "" });
        toast.success(response.message);
        loadHealthRecord();
      } else {
        toast.error(response.message);
      }
    } catch (error: unknown) {
      toast.error(`Error while saving health record: ${error?.message}`);
    } finally {
      setIssaving(false);
    }
  };

  // handle tab swtich for Patient Social History section
  const handleTabSwitch = (tab: string) => {
    setSelectedSocialHistory(tab);
    handleShowForm("", false);
  };

  // Determine if buttons should be shown based on isPatientAvailable and user role
  const showButtons = isPatientAvailable && user?.role !== "admin";

  return (
    <div className="relative border rounded-2xl w-full h-[345px] bg-[#EEF2F5] overflow-hidden">
      <div className="bg-white flex items-center justify-between p-2 px-3 h-[60px]">
        <span className="text-sm flex items-center justify-center gap-3 text-gray-500 font-medium">
          {<Icon className="w-4 h-4" />} {category}
        </span>

        {isReadOnly && !isTreatedPatient && (
          <>
            {showForm.status && showForm.category === category ? (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handleShowForm(category, false)}
                  className="py-2 px-3 font-medium text-xs rounded-full flex items-center justify-center gap-2 bg-[#F0F0F0] outline-none text-black"
                >
                  Cancel
                </button>

                {isEditable ? (
                  <button
                    onClick={() => handleUpdateHealthRecord(showForm.category)}
                    className="py-2 px-3 font-medium text-xs rounded-full flex items-center justify-center gap-2 bg-[#D7FFE7] outline-none text-[#199C4D]"
                    disabled={isLoading}
                  >
                    {isSaving ? "Updating..." : "Update"}
                    {!isSaving && (
                      <CirclePlus className="w-4 h-4 bg-[#199C4D] text-white rounded-full border-none" />
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSaveHealthRecord(showForm.category)}
                    className="py-2 px-3 font-medium text-xs rounded-full flex items-center justify-center gap-2 bg-[#D7FFE7] outline-none text-[#199C4D]"
                    disabled={isLoading}
                  >
                    {isSaving ? "Saving..." : "Save"}
                    {!isSaving && (
                      <CirclePlus className="w-4 h-4 bg-[#199C4D] text-white rounded-full border-none" />
                    )}
                  </button>
                )}
              </div>
            ) : (
              <>
                {showButtons && !isTreatedPatient && (
                  <button
                    onClick={() => handleShowForm(category, true)}
                    className="py-2 px-3 font-medium text-xs rounded-full flex items-center justify-center gap-2 bg-[#D7FFE7] outline-none text-[#199C4D]"
                  >
                    Add
                    <CirclePlus className="w-4 h-4 bg-[#199C4D] text-white rounded-full border-none" />
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>

      {category === "Social History" && (
        <div className="flex items-center justify-between gap-2 p-2">
          {["Smoking", "Drinking", "Tobacco"].map((tab) => {
            return (
              <button
                key={tab}
                onClick={() => handleTabSwitch(tab)}
                className={`bg-white rounded-md text-sm font-medium py-1 px-2 leading-6 flex justify-center items-center gap-2 ${
                  selectedSocialHistory === tab && "text-[#199C4D]"
                }`}
              >
                <span
                  className={`w-[14px] h-[14px] border rounded-sm ${
                    selectedSocialHistory === tab
                      ? "bg-[#199C4D] border-[#199C4D]"
                      : "border-[#EEEEEE] bg-[#FFFFFF]"
                  }`}
                ></span>
                {tab}
              </button>
            );
          })}
        </div>
      )}

      {showForm.status && showForm.category === category ? (
        <RecordForms
          primaryPlaceholder={primaryPlaceholder}
          secondaryPlaceholder={secondaryPlaceholder}
          onChangeFormData={onChangeFormData}
          healthFormData={healthFormData}
          category={category}
        />
      ) : (
        <RecordList
          item={healthRecords}
          isLoading={isLoading}
          loadHealthRecord={loadHealthRecord}
          setHealthFormData={setHealthFormData}
          handleShowForm={handleShowForm}
          setEditable={setEditable}
          category={category}
          selectedSocialHistory={selectedSocialHistory}
          isPatientAvailable={isPatientAvailable} // Pass isPatientAvailable to RecordList
          isReadOnly={isReadOnly}
          isTreatedPatient={isTreatedPatient}
        />
      )}
    </div>
  );
};

//! Form of box to create the health Record
const RecordForms = ({
  primaryPlaceholder,
  secondaryPlaceholder,
  onChangeFormData,
  healthFormData,
  category,
}: RecordFormsPropsType) => {
  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={` rounded-b-2xl hiddenScrollbar py-2 flex-col flex items-center justify-start gap-2 ${
        category === "Social History" ? "h-[68.5%]" : "h-[81.5%] "
      } overflow-hidden w-full overflow-y-auto`}
    >
      {category !== "Social History" && (
        <input
          type="text"
          name="health_value"
          value={healthFormData.health_value}
          className="w-[95%] px-3 py-2 border border-gray-300 outline-none rounded-lg"
          placeholder={primaryPlaceholder}
          onChange={onChangeFormData}
        />
      )}
      {category === "Social History" && (
        <>
          <input
            type="text"
            name="current_state"
            value={healthFormData.current_state}
            className="w-[95%] px-3 py-2 border border-gray-300 outline-none rounded-lg"
            placeholder={"Enter Current State"}
            onChange={onChangeFormData}
          />
          <input
            type="text"
            name="daily_consumption"
            value={healthFormData.daily_consumption}
            className="w-[95%] px-3 py-2 border border-gray-300 outline-none rounded-lg"
            placeholder={"Enter Daily Consumption"}
            onChange={onChangeFormData}
          />
        </>
      )}

      {category !== "Medical Conditions" && (
        <textarea
          name="health_value_description"
          value={healthFormData.health_value_description}
          className=" resize-none h-[120px] w-[95%] px-3 py-2 border border-gray-300 outline-none rounded-lg"
          placeholder={secondaryPlaceholder}
          onChange={onChangeFormData}
        ></textarea>
      )}

      <div className="w-[95%] flex items-center justify-center gap-1">
        <input
          value={healthFormData.start_date}
          name="start_date"
          type="date"
          className={`${
            category === "Social History" ? "w-[50%]" : "w-[100%]"
          } px-3 py-2 border border-gray-300 outline-none rounded-lg`}
          onChange={onChangeFormData}
        />
        {category === "Social History" && (
          <input
            value={healthFormData.end_date}
            name="end_date"
            type="date"
            className={`w-[50%] px-3 py-2 border border-gray-300 outline-none rounded-lg`}
            onChange={onChangeFormData}
          />
        )}
      </div>
    </form>
  );
};

//! List of previous health Records
const RecordList = ({
  item,
  isLoading,
  loadHealthRecord,
  setHealthFormData,
  handleShowForm,
  setEditable,
  category,
  selectedSocialHistory,
  isPatientAvailable, // Add this prop
  isReadOnly,
  isTreatedPatient,
}: RecordListPropsType) => {
  const { user } = useAuth();
  const handleDeleteRecord = async (recordId: string) => {
    try {
      const response = await PatientService.deletePatientHealthRecord(recordId);
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(`Error while saving health record: ${error?.message}`);
    } finally {
      loadHealthRecord();
    }
  };

  const handleEditRecord = (records: HealthRecordFromDataType) => {
    handleShowForm(records.health_parameter, true);
    setEditable(true);

    setHealthFormData({
      id: records?.id,
      health_parameter: records?.health_parameter,
      health_value: records?.health_value,
      health_value_description: records?.health_value_description,
      status: records?.status,
      current_state: records?.current_state,
      daily_consumption: records?.daily_consumption,
      start_date: records?.health_value_start_date?.split("T")[0],
      end_date: records?.health_value_end_date?.split("T")[0],
    });
  };

  // Determine if buttons should be shown based on isPatientAvailable and user role
  const showButtons = isPatientAvailable && user?.role !== "admin";

  const filteredRecords = item?.filter((record) =>
    category === "Social History"
      ? record.health_value === selectedSocialHistory
      : true
  );
  return (
    <ul
      className={`select-none hiddenScrollbar rounded-b-2xl px-2 overflow-hidden overflow-y-auto h-[81.5%] ${
        category === "Social History" ? "h-[68.5%]" : "h-[81.5%] "
      }`}
    >
      {isLoading ? (
        <li className="w-full h-full flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-4" />
        </li>
      ) : filteredRecords?.length === 0 || !item ? (
        <li
          className={`font-bold text-sm w-full h-full flex items-center justify-center capitalize text-[#771D51] ${
            category === "Social History" && "-mt-12"
          }`}
        >
          No{" "}
          {category === "Social History"
            ? `${selectedSocialHistory}`
            : `${category}`}{" "}
          records found
        </li>
      ) : (
        filteredRecords.map((record: HealthRecordResponseType) => (
          <li
            key={record.id}
            className="hoverAction rounded-md bg-white p-4 my-3 text-sm"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">
                {record?.health_value}
              </span>
              <div className="flex items-center  justify-center gap-2">
                <span className="text-[#9E9E9E] text-xs font-medium">
                  {record?.health_value_start_date?.split("T")[0]}
                </span>
                {category === "Social History" && (
                  <span className="text-[#9E9E9E] text-xs font-medium">
                    {record?.health_value_end_date?.split("T")[0]}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="w-[80%] text-[#9E9E9E] text-sm ">
                {record?.health_value_description}
              </span>

              {/* Edit and Delete Button */}
              {showButtons && isReadOnly && !isTreatedPatient && (
                <div className="buttonBox flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleEditRecord(record)}
                    title="Edit Record"
                    className=" text-[#868686] text-sm"
                  >
                    <SquarePen className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteRecord(record?.id)}
                    title="Delete Record"
                    className=" text-[#FF5555] text-sm"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </li>
        ))
      )}
    </ul>
  );
};
